import { storeToRefs } from 'pinia';
import { defineComponent, computed, onMounted } from 'vue';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import { getChannelType, maskString, removeCountryPrefix, } from '@/utils/helpers';
import { PAYOUT_ACCOUNTS_LIMIT } from '@/utils/constants';
import { usePayoutAccountsStore } from '@/store/modules/payout-accounts';
import useTransactionsStore from '@/store/modules/transactions';
import { usePaymentStore } from '@/store/modules/payment';
import { PayoutAccountsTypes } from '@/types/payout-accounts';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
var PayoutComponents = PayoutAccountsTypes.PayoutComponents;
var PayoutFlow = PayoutAccountsTypes.PayoutFlow;
var ChannelTypes = WidgetApiServiceTypes.ChannelTypes;
export default defineComponent({
    name: 'ManagePayouts',
    components: { LoaderComponent },
    setup() {
        const payoutAccountsStore = usePayoutAccountsStore();
        const { currentStep, defaultCountry, error, isMomo, loading, payoutAccounts, payoutFlow, selectedPayoutMethod, fromTransaction, } = storeToRefs(payoutAccountsStore);
        const transactionsStore = useTransactionsStore();
        const { selectedChannel } = storeToRefs(transactionsStore);
        const paymentStore = usePaymentStore();
        const { sellPaymentAccountName, sellPaymentAccountNumber, sellPaymentBranchCode, selectedPaymentNetwork, userMomoAccountNumber, } = storeToRefs(paymentStore);
        const createPayoutAccount = () => {
            if (payoutAccounts.value.length >= PAYOUT_ACCOUNTS_LIMIT) {
                payoutAccountsStore.setError('Limit of payout accounts reached. Delete accounts to add new ones.');
                return;
            }
            payoutFlow.value = PayoutFlow.Create;
            if (fromTransaction.value) {
                // if "use new payout account" option is selected coming from a transaction
                selectedPayoutMethod.value = getChannelType(selectedChannel.value);
                currentStep.value = PayoutComponents.FormSelect;
                return;
            }
            currentStep.value = PayoutComponents.SelectPayout;
        };
        onMounted(async () => {
            payoutAccountsStore.resetActivePayoutAccount();
            await payoutAccountsStore.getPayoutAccounts();
        });
        const onSelectPayoutAccount = async (event, payoutAccount) => {
            event.preventDefault();
            selectedPayoutMethod.value = payoutAccountsStore.getChannel(payoutAccount.accountType);
            payoutAccountsStore.setActivePayoutAccount({
                ...payoutAccount,
                ...(isMomo.value
                    ? {
                        accountNumber: removeCountryPrefix(payoutAccount.accountNumber, defaultCountry.value.label),
                    }
                    : {}),
            });
            if (fromTransaction.value) {
                // if an already saved payout account is selected, go ahead
                // and use the payout account details for the transaction
                sellPaymentAccountName.value = payoutAccount.accountName;
                sellPaymentAccountNumber.value = payoutAccount.accountNumber;
                sellPaymentBranchCode.value = payoutAccount.accountBranchCode;
                userMomoAccountNumber.value = removeCountryPrefix(payoutAccount.accountNumber, defaultCountry.value.label);
                selectedPaymentNetwork.value = { id: payoutAccount.networkId };
                await transactionsStore.getWalletDetails();
            }
            else {
                payoutFlow.value = PayoutFlow.Update;
                currentStep.value = PayoutComponents.FormSelect;
            }
        };
        const serializedAccounts = computed(() => {
            const accounts = fromTransaction.value
                ? payoutAccounts.value.filter(account => account.accountType === selectedPayoutMethod.value)
                : payoutAccounts.value;
            return accounts.map((payoutAccount) => ({
                title: maskString(payoutAccount.accountNumber),
                subtitle: payoutAccount.accountName,
                icon: payoutAccountsStore.getChannel(payoutAccount.accountType) ===
                    ChannelTypes.MOMO
                    ? require('@/assets/payout-accounts/momo.svg')
                    : require('@/assets/payout-accounts/bank.svg'),
                iconAlt: payoutAccount.accountType,
                onClick: (event) => onSelectPayoutAccount(event, payoutAccount),
            }));
        });
        const payoutOptions = computed(() => [
            ...serializedAccounts.value,
            {
                title: 'Add new payout account',
                subtitle: 'Add a new payout account for your sell transaction',
                icon: require('@/assets/payout-accounts/add.svg'),
                iconAlt: 'add payout',
                onClick: createPayoutAccount,
            },
        ].slice(0, PAYOUT_ACCOUNTS_LIMIT)); // hide add button if limit
        return {
            error,
            loading,
            payoutOptions,
        };
    },
});
